import ProfileImage from '../../ProfileImage';
import dayjs from 'dayjs';
import { getFeedBadge } from '../../../lib/feedSource';

const ReceiverChipList = ({ receivers, role, isSchedule }: { receivers: any[]; role?: string; isSchedule: boolean }) => {
    if (!receivers || receivers.length <= 1) return null;
    const suffix = isSchedule ? '회원' : role === 'member' ? '강사' : '회원';
    return (
        <div className="px-[24px] pb-[14px]">
            <div className="flex flex-wrap items-center gap-x-[6px] gap-y-[6px]">
                <span className="text-[12px] text-[#6B7280] font-medium shrink-0">
                    → 전달된 {suffix} {receivers.length}명
                </span>
                {receivers.map((r: any, idx: number) => (
                    <span
                        key={`${r.receiverId || r.receiverName}-${idx}`}
                        className="px-[9px] py-[2px] rounded-[6px] text-[12px]"
                        style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}
                    >
                        {r.receiverName}
                    </span>
                ))}
            </div>
        </div>
    );
};

const getTypeBadge = (feed: any) => {
    const postType = feed?.postType;
    if (postType === 'SCHEDULEREPORT' || postType === 'GROUP_SESSION_LOG') {
        const badge = getFeedBadge(feed);
        return { label: badge.label, color: badge.color, bg: badge.background };
    }
    if (postType === 'FIXED') {
        return { label: '공지', color: '#F59E0B', bg: '#FFFBEB' };
    }
    return { label: '게시물', color: '#6B7280', bg: '#F3F4F6' };
};

const ModalFeedDetailHeader = ({ modalObject, setModalVisible, topBarOnly, profileOnly }: any) => {
    const badge = getTypeBadge(modalObject);
    const isSchedule = modalObject?.postType === 'SCHEDULEREPORT';

    // topBarOnly: X 버튼만
    if (topBarOnly) {
        return (
            <div className="flex items-center justify-end px-[24px] pt-[12px] pb-[8px] border-b border-[#E5E7EB]">
                <button
                    type="button"
                    aria-label="피드 상세 닫기"
                    onClick={() => (typeof setModalVisible === 'function' ? setModalVisible() : setModalVisible(false))}
                    className="w-[32px] h-[32px] flex items-center justify-center rounded-full hover:bg-[#F3F4F6] transition-colors cursor-pointer"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>
        );
    }

    // profileOnly: 배지 + 타임스탬프 + 프로필 정보
    if (profileOnly) {
        return (
            <div className="px-[24px] pt-[16px] pb-[12px] border-b border-[#E5E7EB]">
                {/* 배지 + 타임스탬프 */}
                <div className="flex items-center gap-[10px] mb-[12px]">
                    <span
                        className="px-[10px] py-[3px] rounded-[6px] text-[12px] font-semibold"
                        style={{ backgroundColor: badge.bg, color: badge.color }}
                    >
                        {badge.label}
                    </span>
                    <span className="text-[11px] text-[#9CA3AF]">
                        {dayjs(modalObject?.createdTime).format('YYYY.MM.DD(ddd) A h:mm')}
                    </span>
                </div>
                {/* 프로필 */}
                <div className="flex items-center gap-[12px]">
                <ProfileImage
                    imageUrl={modalObject?.profileImageUrl}
                    type="Middle"
                    profileName={modalObject?.profileName}
                    profileColorType={modalObject?.profileColorType}
                    right={0}
                />
                <div className="flex flex-col">
                    <span className="text-[15px] font-bold text-[#1F2937]">
                        {modalObject?.writerName}
                        <span className="text-[13px] font-normal text-[#6B7280] ml-[6px]">
                            {modalObject?.role === 'coach' ? '강사' : '회원'}
                        </span>
                    </span>
                    <span className="text-[12px] text-[#9CA3AF]">
                        {modalObject?.role === 'coach'
                            ? `회원 총 ${modalObject?.totalMemberCount || 0}명`
                            : `${modalObject?.receivers?.[0]?.receiverName} 강사 담당`}
                    </span>
                </div>

                {/* 단일 수신자일 때만 인라인 pill — 다수는 아래 chip 리스트에서 표시 */}
                {modalObject?.receivers?.length === 1 && (
                    <div className="ml-auto flex items-center gap-[6px] bg-[#F0F7FF] px-[12px] py-[6px] rounded-[8px]">
                        <span className="text-[13px] font-medium text-[#3B82F6]">
                            → {modalObject.receivers[0].receiverName}
                            {isSchedule ? ' 회원' : (modalObject.role === 'member' ? ' 강사' : ' 회원')}
                        </span>
                    </div>
                )}
                </div>

                {/* 다수 전달 시 전체 회원 chip 목록 */}
                <ReceiverChipList
                    receivers={modalObject?.receivers}
                    role={modalObject?.role}
                    isSchedule={isSchedule}
                />
            </div>
        );
    }

    // 전체 (기본 - 둘 다 렌더링)
    return (
        <div
            className="w-full bg-white border-b border-[#E5E7EB]"
            style={{ minHeight: 'auto' }}
        >
            {/* 상단 바: 타이틀 + 닫기 */}
            <div className="flex items-center justify-between px-[24px] pt-[20px] pb-[12px]">
                <div className="flex items-center gap-[10px]">
                    <span
                        className="px-[10px] py-[3px] rounded-[6px] text-[12px] font-semibold"
                        style={{ backgroundColor: badge.bg, color: badge.color }}
                    >
                        {badge.label}
                    </span>
                    <span className="text-[11px] text-[#9CA3AF]">
                        {dayjs(modalObject?.createdTime).format('YYYY.MM.DD(ddd) A h:mm')}
                    </span>
                </div>
                <button
                    type="button"
                    aria-label="피드 상세 닫기"
                    onClick={() => (typeof setModalVisible === 'function' ? setModalVisible() : setModalVisible(false))}
                    className="w-[32px] h-[32px] flex items-center justify-center rounded-full hover:bg-[#F3F4F6] transition-colors cursor-pointer"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* 프로필 정보 */}
            <div className="flex items-center gap-[12px] px-[24px] pb-[16px]">
                <ProfileImage
                    imageUrl={modalObject?.profileImageUrl}
                    type="Middle"
                    profileName={modalObject?.profileName}
                    profileColorType={modalObject?.profileColorType}
                    right={0}
                />
                <div className="flex flex-col">
                    <span className="text-[15px] font-bold text-[#1F2937]">
                        {modalObject?.writerName}
                        <span className="text-[13px] font-normal text-[#6B7280] ml-[6px]">
                            {modalObject?.role === 'coach' ? '강사' : '회원'}
                        </span>
                    </span>
                    <span className="text-[12px] text-[#9CA3AF]">
                        {modalObject?.role === 'coach'
                            ? `회원 총 ${modalObject?.totalMemberCount || 0}명`
                            : `${modalObject?.receivers?.[0]?.receiverName} 강사 담당`}
                    </span>
                </div>

                {/* 단일 수신자일 때만 인라인 pill — 다수는 아래 chip 리스트에서 표시 */}
                {modalObject?.receivers?.length === 1 && (
                    <div className="ml-auto flex items-center gap-[6px] bg-[#F0F7FF] px-[12px] py-[6px] rounded-[8px]">
                        <span className="text-[13px] font-medium text-[#3B82F6]">
                            → {modalObject.receivers[0].receiverName}
                            {isSchedule ? ' 회원' : (modalObject.role === 'member' ? ' 강사' : ' 회원')}
                        </span>
                    </div>
                )}
            </div>

            {/* 다수 전달 시 전체 회원 chip 목록 */}
            <ReceiverChipList
                receivers={modalObject?.receivers}
                role={modalObject?.role}
                isSchedule={isSchedule}
            />
        </div>
    );
};

export default ModalFeedDetailHeader;
